import ValidateModule from "./ValidateModule";
import SecurityAdminModule from "./security/SecurityAdminModule";
import SecurityEntitlementsModule from "./security/SecurityEntitlementsModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const es = global.es;

/**
 * @class SecurityModule
 */
export default class SecurityModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    admin: SecurityAdminModule;
    entitlements;
    
    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.admin = new SecurityAdminModule(eventChannel, rqBroker, resourceName + '/Admin', cache);
        this.entitlements = new SecurityEntitlementsModule(eventChannel, rqBroker, resourceName + '/Entitlements', cache);

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.getEntitlementTypes = this.getEntitlementTypes.bind(this);
        this.getOwnershipOfWorkspace = this.getOwnershipOfWorkspace.bind(this);
        this.getOwnershipOfGeoMap = this.getOwnershipOfGeoMap.bind(this);
        this.getOwnershipOfLayerView = this.getOwnershipOfLayerView.bind(this);
        this.getOwnershipOfLayerMeta = this.getOwnershipOfLayerMeta.bind(this);
        this.getOwnership = this.getOwnership.bind(this);
        this.getOwnerOfWorkspace = this.getOwnerOfWorkspace.bind(this);
        this.getOwnerOfGeoMap = this.getOwnerOfGeoMap.bind(this);
        this.getOwnerOfLayerView = this.getOwnerOfLayerView.bind(this);
        this.getOwnerOfLayerMeta = this.getOwnerOfLayerMeta.bind(this);
        this.getOwnerOfEntity = this.getOwnerOfEntity.bind(this);
        this.getOwner = this.getOwner.bind(this);
        this.isOwnerOfWorkspace = this.isOwnerOfWorkspace.bind(this);
        this.isOwnerOfGeoMap = this.isOwnerOfGeoMap.bind(this);
        this.isOwnerOfLayerView = this.isOwnerOfLayerView.bind(this);
        this.isOwnerOfLayerMeta = this.isOwnerOfLayerMeta.bind(this);
        this.isOwnerOfEntity = this.isOwnerOfEntity.bind(this);
        this.isOwner = this.isOwner.bind(this);
        this.getEntitlementsGeoMap = this.getEntitlementsGeoMap.bind(this);
        this.getEntitlementsWorkspace = this.getEntitlementsWorkspace.bind(this);
        this.getEntitlementsLayerMeta = this.getEntitlementsLayerMeta.bind(this);
        this.getEntitlementsGeoMapTemplate = this.getEntitlementsGeoMapTemplate.bind(this);
        this.getEntitlements = this.getEntitlements.bind(this);
        this.getShareStatus = this.getShareStatus.bind(this);
        this.getGeoMapShareStatus = this.getGeoMapShareStatus.bind(this);
        this.getLayerMetaShareStatus = this.getLayerMetaShareStatus.bind(this);
        this.getGeoMapTemplateOwners = this.getGeoMapTemplateOwners.bind(this);
        this.getPermissions = this.getPermissions.bind(this);
        this.getDisabledPermissions = this.getDisabledPermissions.bind(this);
        this.isEditableLayerView = this.isEditableLayerView.bind(this);
        this.isEditableLayerMeta = this.isEditableLayerMeta.bind(this);
        this.isEditableGeoMap = this.isEditableGeoMap.bind(this);
        this.isConfigurableGeoMap = this.isConfigurableGeoMap.bind(this);
        this.isConfigurableWorkspace = this.isConfigurableWorkspace.bind(this);
        this.isConfigurableLayerView = this.isConfigurableLayerView.bind(this);
        this.isConfigurable = this.isConfigurable.bind(this);
        this.isAccessibleLayerMeta = this.isAccessibleLayerMeta.bind(this);
        this.isAccessibleLayerView = this.isAccessibleLayerView.bind(this);
        this.isAccessibleGeoMap = this.isAccessibleGeoMap.bind(this);
        this.isAccessibleWorkspace = this.isAccessibleWorkspace.bind(this);
        this.isAccessible = this.isAccessible.bind(this);
        this.getEditableLayerViewIdsByGeoMapId = this.getEditableLayerViewIdsByGeoMapId.bind(this);
        this.getEditableLayerMetaIdsByGeoMapId = this.getEditableLayerMetaIdsByGeoMapId.bind(this);
        this.getGroups = this.getGroups.bind(this);
        this.getUserPrivateGroups = this.getUserPrivateGroups.bind(this);
        this.getUsers = this.getUsers.bind(this);
        this.requestAIChat = this.requestAIChat.bind(this);
    }

    /**
     * 
     * @returns 
     */
    getEntitlementTypes() {
        return EntitlementType;
    }

    /**
     * getOwnershipOfWorkspace
     * 
     * @param opts 
     * 
     * @returns
     */
    getOwnershipOfWorkspace(opts?: ApiOpts) {
        return this.getOwnership(EntitlementType.WORKSPACE, opts);
    }
    
    /**
     * getOwnershipOfGeoMap
     * 
     * @param opts 
     * 
     * @returns
     */
    getOwnershipOfGeoMap(opts?: ApiOpts) {
        return this.getOwnership(EntitlementType.GEOMAP, opts);
    }
    
    /**
     * getOwnershipOfLayerView
     * 
     * @param opts 
     * 
     * @returns
     */
    getOwnershipOfLayerView(opts?: ApiOpts) {
        return this.getOwnership(EntitlementType.LAYER_VIEW, opts);
    }
    
    /**
     * getOwnershipOfLayerMeta
     * 
     * @param opts 
     * 
     * @returns
     */
    getOwnershipOfLayerMeta(opts?: ApiOpts) {
        return this.getOwnership(EntitlementType.LAYER_META, opts);
    }
    
    /**
     * Get all the groups for tenant from the server
     * 
     * @param modelType one of: "workspace", "geomap", "layerview", "layermeta"
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    private getOwnership(modelType: string, opts?: ApiOpts) {
        if (es.isPublicAccess) {
            // this request is about to fail as public user cannot access this resource
            es.log.error('public user cannot access this resource [' + modelType + ']');
        }

        const url = es.api.getURL(this.resourceName) + "/ownership/" + modelType;
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "ownership_" + modelType, opts);
    }

    /**
     * getOwnerOfWorkspace
     * 
     * @param entityId 
     * @param opts 
     * 
     * @returns
     */
    getOwnerOfWorkspace(entityId: number, opts?: ApiOpts) {
        return this.getOwner(EntitlementType.WORKSPACE, entityId, opts);
    }

    /**
     * getOwnerOfGeoMap
     * 
     * @param entityId 
     * @param opts 
     * 
     * @returns
     */
    getOwnerOfGeoMap(entityId: number, opts?: ApiOpts) {
        return this.getOwner(EntitlementType.GEOMAP, entityId, opts);
    }

    /**
     * getOwnerOfLayerView
     * 
     * @param entityId 
     * @param opts 
     * 
     * @returns
     */
    getOwnerOfLayerView(entityId: number, opts?: ApiOpts) {
        return this.getOwner(EntitlementType.LAYER_VIEW, entityId, opts);
    }

    /**
     * getOwnerOfLayerMeta
     * 
     * @param entityId 
     * @param opts 
     * 
     * @returns
     */
    getOwnerOfLayerMeta(entityId: number, opts?: ApiOpts) {
        return this.getOwner(EntitlementType.LAYER_META, entityId, opts);
    }

    /**
     * 
     * @param modelType 
     * @param entityId 
     * @param opts 
     * 
     * @returns 
     */
    getOwnerOfEntity(modelType: string, entityId: number, opts?: ApiOpts) {
        return this.getOwner(modelType, entityId, opts);
    }

    /**
     * Get userType owner of entity. Returns EntityOwnerUserInfo object.
     *
     * @param modelType
     * @param entityId
     * @param opts
     * 
     * @returns a dfd.promise
     */
    private getOwner(modelType: string, entityId: number, opts?: ApiOpts) {
        if (es.isPublicAccess){
            es.log.error('public user cannot access this resource [' + modelType + ']');
        } else {
            const url = es.api.getURL(this.resourceName) + "/owners/" + modelType + "/" + entityId;
            //owners/{resourceType}/{id}
            return this.cache.doGet(this.rqBroker, this.resourceName, url, "owners_" + modelType +'_' + entityId, opts);
        }
    }
    
    /**
     * isOwnerOfWorkspace
     * 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    isOwnerOfWorkspace(ids: number | number[], opts?: ApiOpts) {
        return this.isOwner(EntitlementType.WORKSPACE, ids, opts);
    }
    
    /**
     * isOwnerOfGeoMap
     * 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    isOwnerOfGeoMap(ids: number | number[], opts?: ApiOpts) {
        return this.isOwner(EntitlementType.GEOMAP, ids, opts);
    }
    
    /**
     * isOwnerOfLayerView
     * 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    isOwnerOfLayerView(ids: number | number[], opts?: ApiOpts) {
        return this.isOwner(EntitlementType.LAYER_VIEW, ids, opts);
    }
    
    /**
     * isOwnerOfLayerMeta
     * 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    isOwnerOfLayerMeta(ids: number | number[], opts?: ApiOpts) {
        return this.isOwner(EntitlementType.LAYER_META, ids, opts);
    }

    /**
     * isOwnerOfEntity
     * 
     * @param modelType 
     * @param ids 
     * @param opts 
     * 
     * @returns 
     */
    isOwnerOfEntity(modelType: string, ids: number | number[], opts?: ApiOpts) {
        return this.isOwner(modelType, ids, opts);
    }

    /**
     * isOwnerEntities
     * 
     * @param modelType
     * @param ids resource id(s)
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    private isOwner(modelType: string, ids: number | number[], opts?: ApiOpts) {
        if (Array.isArray(ids)) {
            if (!ValidateModule.checkIds(ids)) {
                return es.util.deferred.rejected();
            }
        } else {
            if (!ValidateModule.checkId(ids)) {
                return es.util.deferred.rejected();
            }
        }
        
        if (es.isPublicAccess) {
            // this request is about to fail as public user cannot access this resource never mind determine owner
            // for all intents and purposes this just means no so just instantly resolve as false (see also: TOR9-7500)
            return es.util.deferred.resolved(false);
        }
        
        const isByIdCall = !Array.isArray(ids);
        if (isByIdCall) {
            ids = [ids];
        }
        
        const url = es.api.getURL(this.resourceName) + "/ownerships/" + modelType + "?ids=" + es.util.url.stringify(ids);

        return this.cache.doGet(this.rqBroker, this.resourceName + "_ownerships_" + modelType, url, ids, opts).then((rs) => {
            if (isByIdCall) {
                return rs[0];
            }
            
            return rs;
        });
    }
    
    /**
     * getEntitlementsGeoMap
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getEntitlementsGeoMap(id: number | number[], opts?: ApiOpts) {
        return this.getEntitlements(EntitlementType.GEOMAP, id, opts);
    }

    /**
     * getEntitlementsWorkspace
     *
     * @param id workspace id
     * @param opts
     * 
     * @returns
     */
    getEntitlementsWorkspace(id: number | number[], opts?: ApiOpts) {
        return this.getEntitlements(EntitlementType.WORKSPACE, id, opts);
    }

    /**
     * getEntitlementsLayerMeta
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getEntitlementsLayerMeta(id: number | number[], opts?: ApiOpts) {
        return this.getEntitlements(EntitlementType.LAYER_META, id, opts);
    }
    
    /**
     * getEntitlementsLayerMeta
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    getEntitlementsGeoMapTemplate(id: number | number[], opts?: ApiOpts) {
        return this.getEntitlements(EntitlementType.GEOMAP_TEMPLATE, id, opts);
    }

    /**
     * Get all the entitlements for the GeoMap
     * 
     * @param modelType one of: "workspace", "geomap", "layerview", "layermeta"
     * @param ids
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    private getEntitlements(modelType: string, ids: number | number[], opts?: ApiOpts) {
        // if not given any ids then just resolve with empty array
        if (!ids || ids.length == 0) {
            return es.util.deferred.resolved([]);
        }
        
        // This function decides on one of three different REST urls to call:
        // 1) Single GET e.g. /entitlements/workspace/101
        // 2) Multi  GET e.g. /entitlements/workspace?ids=[101,102]
        // 2) Multi  POST e.g. /entitlements/workspace {body == [101,102...]}
        let url;
        if (Array.isArray(ids)) {
            if (!ValidateModule.checkIds(ids)) {
                return es.util.deferred.rejected();
            }

            if(ids.length == 1){
                url = es.api.getURL(this.resourceName) + '/entitlements/' + modelType + '/' + ids;
            } else{
                url = es.api.getURL(this.resourceName) + '/entitlements/' + modelType + '?ids=' + es.util.url.stringify(ids);
            }
        } else {
            if (!ValidateModule.checkId(ids)) {
                return es.util.deferred.rejected();
            }

            url = es.api.getURL(this.resourceName) + '/entitlements/' + modelType + '/' + ids;
        }

        if (url.length > this.rqBroker.MAX_URL_LENGTH) {
            url = es.api.getURL(this.resourceName) + '/entitlements/bulk/' + modelType;

            return this.rqBroker.doPost({
                url: url,
                opts: opts,
                data: ids
            });
        }

        return this.cache.doGet(this.rqBroker, this.resourceName, url, "entitlements_" + modelType + "_" + es.util.url.stringify(ids), opts);
    }

    /**
     * getShareStatus
     * 
     * @param modelType
     * @param ids resource id(s)
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    private getShareStatus(modelType: string, ids: number | number[], opts?: ApiOpts) {
        // if not given any ids then just resolve with empty array
        if (!ids || ids.length == 0) {
            return es.util.deferred.resolved([]);
        }
        
        if (Array.isArray(ids)) {
            if (!ValidateModule.checkIds(ids)) {
                return es.util.deferred.rejected();
            }
        } else {
            if (!ValidateModule.checkId(ids)) {
                return es.util.deferred.rejected();
            }
            ids = [ids];
        }
        
        const url = es.api.getURL(this.resourceName) + '/entitlements/' + modelType + '/sharestatus?ids=' + es.util.url.stringify(ids);

        if (url.length > this.rqBroker.MAX_URL_LENGTH) {
            return this.rqBroker.doPost({
                url: es.api.getURL(this.resourceName) + "/entitlements/bulk/" + modelType + "/sharestatus",
                opts: opts,
                data: ids
            });
        }

        return this.cache.doGet(this.rqBroker, this.resourceName, url, 'entitlements_' + modelType + '_sharestatus_' + es.util.url.stringify(ids), opts);
    }
    
    /**
     * get ShareStatus on the specified GeoMapIds
     * 
     * @param geoMapIds id(s) from GeoMap(s)
     * @param opts
     * 
     * @returns a jquery deferred object resolved when request is processed.
     */
    getGeoMapShareStatus(geoMapIds: number | number[], opts?: ApiOpts) {
        return this.getShareStatus(EntitlementType.GEOMAP, geoMapIds, opts);
    }
    
    /**
     * get ShareStatus on the specified GeoMapIds
     * 
     * @param layerMetaIds id(s) from LayerMeta(s)
     * @param opts
     * 
     * @returns a jquery deferred object resolved when request is processed.
     */
    getLayerMetaShareStatus(layerMetaIds: number | number[], opts?: ApiOpts) {
        return this.getShareStatus(EntitlementType.LAYER_META, layerMetaIds, opts); // TODO FIXME Was calling rqBroker instead of cache... Any reason?!
    }

    /**
     * get ShareStatus on the specified GeoMapIds
     * 
     * @param geoMapIds id(s) from GeoMap(s)
     * 
     * @returns a jquery deferred object resolved when request is processed.
     */
    getGeoMapTemplateOwners(geoMapTemplateIds: number | number[], opts?: ApiOpts) {
        // if not given any ids then just resolve with empty array
        if (!geoMapTemplateIds || geoMapTemplateIds.length == 0) {
            return es.util.deferred.resolved([]);
        }

        if (Array.isArray(geoMapTemplateIds)) {
            if (!ValidateModule.checkIds(geoMapTemplateIds)) {
                return es.util.deferred.rejected();
            }
        } else {
            if (!ValidateModule.checkId(geoMapTemplateIds)) {
                return es.util.deferred.rejected();
            }
        }

        const url = es.api.getURL(this.resourceName) + "/owners/geomap_template?ids=" + es.util.url.stringify(geoMapTemplateIds);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "owners_geomaptemplate_" + geoMapTemplateIds, opts);
        // return rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * getPermissions
     * 
     * @param opts
     * 
     * @returns
     */
    getPermissions(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/permissions";
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "permissions", opts);
        //return rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * getDisabledPermissions
     * 
     * @param opts
     * 
     * @returns
     */
    getDisabledPermissions(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/permissions/disabled";
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "permissions_disabled", opts);
        //return rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * isEditableLayerView
     * 
     * @param id
     * @param opts
     * 
     * @returns
     */
    isEditableLayerView(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/entitlements/" + EntitlementType.LAYER_VIEW + "/is_editable/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "entitlements_" + EntitlementType.LAYER_VIEW + "_iseditable_" + id, opts);
        //return rqBroker.doGet({url: url});
    }


    /**
     * isEditableLayerMeta
     * 
     * @param id
     * @param opts
     * 
     * @returns
     */
    isEditableLayerMeta(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/entitlements/" + EntitlementType.LAYER_META + "/is_editable/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "entitlements_" + EntitlementType.LAYER_META + "_iseditable_" + id, opts);
        //return rqBroker.doGet({url: url});
    }

    /**
     * isEditableGeoMap
     * 
     * @param id
     * @param opts
     * 
     * @returns
     */
    isEditableGeoMap(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/entitlements/" + EntitlementType.GEOMAP + "/is_editable/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "entitlements_" + EntitlementType.GEOMAP + "_iseditable_" + id, opts);
        //return rqBroker.doGet({url: url});
    }

    /**
     * Returns true if user is owner of geo map or is member of group which has configure entitlement of this geo map
     * 
     * @param id geomap id
     * @param opts
     * 
     * @returns
     */
    isConfigurableGeoMap(id: number, opts?: ApiOpts) {
        return this.isConfigurable(EntitlementType.GEOMAP, id, opts);
    }
    
    /**
     * Returns true if user is owner of workspace or is member of group which has configure entitlement of this workspace
     * 
     * @param id geomap id
     * @param opts
     * 
     * @returns
     */
    isConfigurableWorkspace(id: number, opts?: ApiOpts) {
        return this.isConfigurable(EntitlementType.WORKSPACE, id, opts);
    }
    
    /**
     * Returns true if user is owner of layer view is member of group which has configure entitlement of this layer view
     * 
     * @param id geomap id
     * @param opts
     * 
     * @returns
     */
    isConfigurableLayerView(id: number, opts?: ApiOpts) {
        return this.isConfigurable(EntitlementType.LAYER_VIEW, id, opts);
    }
    
    /**
     * Returns true if user is owner of entity or is member of group which has configure entitlement of this entity
     * 
     * @param modelType entitlements modelType
     * @param id geomap id
     * @param opts
     * 
     * @returns
     */
    private isConfigurable(modelType: string, id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/entitlements/" + modelType + "/is_configurable/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "entitlements_" + modelType + "_is_configurable_" + id, opts);
        //return rqBroker.doGet({url: url});
    }
    
    /**
     * isAccessibleLayerMeta
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    isAccessibleLayerMeta(id: number, opts?: ApiOpts) {
        return this.isAccessible(EntitlementType.LAYER_META, id, opts);
    }
    
    /**
     * isAccessibleLayerView
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    isAccessibleLayerView(id: number, opts?: ApiOpts) {
        return this.isAccessible(EntitlementType.LAYER_VIEW, id, opts);
    }
    
    /**
     * isAccessibleGeoMap
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    isAccessibleGeoMap(id: number, opts?: ApiOpts) {
        return this.isAccessible(EntitlementType.GEOMAP, id, opts);
    }
    
    /**
     * isAccessibleWorkspace
     * 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    isAccessibleWorkspace(id: number, opts?: ApiOpts) {
        return this.isAccessible(EntitlementType.WORKSPACE, id, opts);
    }
    
    /**
     * isAccessible
     * 
     * @param modelType 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    private isAccessible(modelType:string, id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/accessible/" + modelType + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "accessible_" + modelType + "_" + id, opts);
    }

    /**
     * getEditableLayerViewIdsByGeoMapId
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getEditableLayerViewIdsByGeoMapId(geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }
        const url = es.api.getURL(this.resourceName) + "/entitlements/" + EntitlementType.LAYER_VIEW + "/editable_by_geomap/" + geoMapId;
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * getEditableLayerMetaIdsByGeoMapId
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getEditableLayerMetaIdsByGeoMapId(geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/entitlements/" + EntitlementType.LAYER_META + "/editable_by_geomap/" + geoMapId;
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * Get all the groups for tenant from the server
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getGroups() {
        const url = es.api.getURL(this.resourceName) + "/groups/tenant";
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "_groups_tenant");
    }
    
    /**
     * List of all users user private security groups.
     * 
     * @returns a dfd.promise
     */
    getUserPrivateGroups() {
        const url = es.api.getURL(this.resourceName) + "/user_private_groups";
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "_groups_user_private");
    }
    
    /**
     * Get all the users for tenant from the server
     * 
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getUsers(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/users";
        //return rqBroker.doGet({url: url});
        return this.cache.doGet(this.rqBroker, this.resourceName, url, "users", opts);
    }

    /**
     * user request AIChat.
     *
     * @returns a dfd promise
     */
    requestAIChat() {
        const url = es.api.getURL(this.resourceName) + "/request_ai_chat";
        return this.rqBroker.doPost({
            'url': url,
        });
    }
}

// Entitlement types
export const EntitlementType = {
    WORKSPACE: "workspace",
    GEOMAP: "geomap",
    LAYER_META: "layermeta",
    LAYER_VIEW: "layerview",
    GEOMAP_TEMPLATE: "geomap_template"
};
